
import { useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { createLocalDateTime, parseStoredDateTime } from "@/utils/timeUtils";
import { useToast } from "@/hooks/use-toast";
import { sendAdminNotification, sendVendorNotification, sendAdminSmsNotification } from "@/data/services";
import { createTravelBlock } from "@/components/booking/utils/travelBlockUtils";
import { sendCustomerNotification } from "@/data/services/customerNotifications";
import { useUserCompleteProfile } from "@/hooks/useUserCompleteProfile";
import { useProfileCompleteness } from "@/hooks/useProfileCompleteness";

export const useBookingSubmission = (
  serviceId: number,
  serviceName: string,
  onClose: () => void
) => {
  const [loading, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { profile: userProfile } = useUserCompleteProfile();
  const { isComplete, isLoading: profileLoading, updateCompletionStatus } = useProfileCompleteness();

  // Process notifications and travel blocks asynchronously
  const processBookingExtras = async (
    bookingId: number,
    bookingData: any,
    startDateTime: Date,
    endDateTime: Date,
    note: string | null,
    userProfile: any,
    session: any
  ) => {
    try {
      console.log('=== PROCESSING BOOKING EXTRAS ===');
      console.log('Booking ID:', bookingId);
      console.log('Service ID:', serviceId);
      
      // Create travel blocks asynchronously
      if (bookingId) {
        console.log('Creating travel blocks for booking ID:', bookingId);
        createTravelBlock(
          bookingId, 
          startDateTime, 
          endDateTime, 
          serviceId, 
          userProfile
        ).catch(error => console.error('Error creating travel block:', error));
      }

      // Start all notifications in parallel
      const notificationPromises = [];

      // Admin notification
      console.log("=== STARTING ADMIN NOTIFICATION ===");
      const adminNotificationDetails = {
        serviceName: serviceName,
        userName: userProfile?.display_name || session?.user?.email?.split('@')[0] || 'Anonymous',
        userEmail: session.user.email,
        details: {
          date: format(startDateTime, "yyyy-MM-dd"),
          time: `${format(startDateTime, "HH:mm")} - ${format(endDateTime, "HH:mm")}`,
          note: note || "No additional notes"
        }
      };
      
      notificationPromises.push(
        sendAdminNotification('booking', adminNotificationDetails)
          .catch(error => console.error("Admin notification error:", error))
      );
      
      // Vendor notification
      console.log("=== STARTING VENDOR NOTIFICATION ===");
      console.log("Vendor notification data:", {
        serviceId,
        bookingId,
        customerName: adminNotificationDetails.userName,
        customerEmail: adminNotificationDetails.userEmail
      });
      
      notificationPromises.push(
        sendVendorNotification('booking', serviceId, {
          bookingId: bookingId,
          customerName: adminNotificationDetails.userName,
          customerEmail: adminNotificationDetails.userEmail,
          customerPhone: userProfile?.phone || null,
          customerAddress: userProfile?.address || null,
          dateTime: format(startDateTime, "yyyy-MM-dd HH:mm"),
          endTime: format(endDateTime, "HH:mm"),
          note: note || "No additional notes"
        }).then(result => {
          console.log("Vendor notification result:", result);
          return result;
        }).catch(error => {
          console.error("Vendor notification error:", error);
          return false;
        })
      );
      
      // Customer notification
      console.log("=== STARTING CUSTOMER NOTIFICATION ===");
      const customerName = userProfile?.display_name || session?.user?.email?.split('@')[0] || 'Anonymous';
      const customerEmail = userProfile?.email || session.user.email;
      
      notificationPromises.push(
        sendCustomerNotification('booking', {
          customerName,
          customerEmail,
          serviceName,
          date: format(startDateTime, "EEEE, MMMM d, yyyy"),
          time: `${format(startDateTime, "HH:mm")} - ${format(endDateTime, "HH:mm")}`,
          location: userProfile?.address || null,
          note: note || null,
          bookingId,
          status: 'new'
        }).catch(error => console.error("Customer notification error:", error))
      );

      // Admin SMS notification
      console.log("=== STARTING ADMIN SMS NOTIFICATION ===");
      notificationPromises.push(
        sendAdminSmsNotification('booking', {
          serviceName,
          userName: customerName,
          userEmail: customerEmail,
          details: {
            date: format(startDateTime, "EEEE, MMMM d, yyyy"),
            time: `${format(startDateTime, "HH:mm")} - ${format(endDateTime, "HH:mm")}`,
            bookingId
          }
        }).catch(error => console.error("Admin SMS notification error:", error))
      );
      
      // Process all notifications in parallel
      console.log("=== PROCESSING ALL NOTIFICATIONS ===");
      const notificationResults = await Promise.allSettled(notificationPromises);
      console.log('Notification results:', notificationResults);
      
      // Check if any notifications failed to show a toast if needed
      const failedNotifications = notificationResults.filter(result => result.status === 'rejected');
      if (failedNotifications.length > 0) {
        console.warn(`${failedNotifications.length} notifications failed to send`);
      }
      
      console.log('=== BOOKING EXTRAS COMPLETED ===');
      return true;
    } catch (error) {
      console.error('=== ERROR IN BOOKING EXTRAS ===');
      console.error('Error in processBookingExtras:', error);
      return false;
    }
  };

  const handleBooking = async (
    isAuthenticated: boolean,
    selectedDate: Date | undefined,
    startTime: string,
    endTime: string,
    note: string,
    conflictError: string | null,
    resetForm: () => void
  ) => {
    if (!isAuthenticated) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please sign in to book an appointment"
      });
      return;
    }

    if (!selectedDate || !startTime || !endTime) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select date and time for your appointment",
      });
      return;
    }

    if (conflictError) {
      toast({
        variant: "destructive",
        title: "Time Conflict",
        description: conflictError,
      });
      return;
    }
    
    // Use centralized profile completeness check
    if (!isComplete) {
      toast({
        variant: "destructive",
        title: "Profile Incomplete",
        description: "Please complete your contact information (display name, phone, address, and location) to book services.",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("Session expired. Please sign in again.");
      }
      
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      
      // Use simplified local time approach - no timezone conversion
      const { date: dateStr, time: startTimeStr } = createLocalDateTime(selectedDate, startTime);
      const { time: endTimeStr } = createLocalDateTime(selectedDate, endTime);
      
      console.log(`📅 Booking creation (local): ${dateStr} ${startTimeStr} - ${endTimeStr}`);
      
      // Create local Date objects for notification formatting
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);
      
      const startDateTime = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), startHours, startMinutes, 0, 0);
      const endDateTime = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), endHours, endMinutes, 0, 0);
      
      console.log(`🕐 Notification formatting: ${startTime} -> ${format(startDateTime, 'HH:mm')}, ${endTime} -> ${format(endDateTime, 'HH:mm')}`);

      // Core booking data using new simplified columns
      const bookingData = {
        service_id: serviceId,
        booking_date: dateStr,
        start_time_local: startTimeStr,
        end_time_local: endTimeStr,
        // Keep legacy columns for backward compatibility during transition
        date: formattedDate,
        start_time: format(startDateTime, "yyyy-MM-dd HH:mm:ss"),
        end_time: format(endDateTime, "yyyy-MM-dd HH:mm:ss"),
        customer_id: session.user.id,
        customer_name: userProfile?.display_name || session.user.email?.split('@')[0] || 'Anonymous',
        customer_email: userProfile?.email || session.user.email,
        customer_phone: userProfile?.phone || null,
        customer_address: userProfile?.address || null,
        note: note || null,
        status: 'new'
      };

      console.log('Creating booking with data:', bookingData);

      // Final conflict check using new simplified approach
      const { data: conflictingBookings, error: conflictError } = await supabase
        .from('bookings')
        .select('id')  
        .eq('service_id', serviceId)
        .or(`and(booking_date.eq.${dateStr},start_time_local.lt.${endTimeStr},end_time_local.gt.${startTimeStr}),and(start_time.lt.${format(endDateTime, "yyyy-MM-dd HH:mm:ss")},end_time.gt.${format(startDateTime, "yyyy-MM-dd HH:mm:ss")})`)
        .not('status', 'in', '(rejected,cancelled)');
        
      if (conflictError) throw conflictError;
      
      if (conflictingBookings && conflictingBookings.length > 0) {
        throw new Error("This time slot is no longer available. Please select a different time.");
      }

      // Create the booking - only core operation
      const { data, error } = await supabase
        .from('bookings')
        .insert(bookingData)
        .select('id');

      if (error) {
        console.error('Booking error:', error);
        throw error;
      }

      const bookingId = data?.[0]?.id;
      console.log('Booking created successfully with ID:', bookingId);
      
      // Show success message immediately after core operation succeeds
      toast({
        title: "Success",
        description: "Your booking has been received. You should receive a notification shortly.",
      });
      
      // Close the dialog and reset form immediately for better UX
      resetForm();
      onClose();
      
      // Process all other operations asynchronously
      if (bookingId) {
        processBookingExtras(
          bookingId,
          bookingData,
          startDateTime,
          endDateTime,
          note || null,
          userProfile,
          session
        ).catch(error => {
          console.error('Error in async booking operations:', error);
        });
      }
      
    } catch (error: any) {
      console.error('Booking error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to create booking. Please try again.",
      });
    } finally {
      // Always reset loading state in the finally block
      setIsSubmitting(false);
    }
  };

  return { handleBooking, loading };
};
